import React from "react";
import {useTranslation} from "react-i18next";
import { Link } from "react-router-dom";

import SearchHeading from "@/components/SearchAndResultShared/SearchHeading";
import { CompaniesResult, Company } from "@/app/pages/businessDirectory/IBusinessDirectory";
import { ISearchContact, ISearchDataServletResponse } from "@/app/pages/contactDirectory/TabContactSearch";
import { usePageURL } from "@/shared/customHooks";
import { isNotUndefined, setCompanyInfoCookie } from "@/shared/FunctionCore";
import { CompanyInformationForCookie } from "@/app/pages/businessDirectory/IBusinessDirectory";
import { ukDunsNumberUrl } from "@/shared/constant";

type IresultData = CompaniesResult | ISearchDataServletResponse;
interface SearchResultProps {
  resultData: IresultData | undefined;
  paginationComponent: React.ReactNode;
  forComponent: string,
  inputSearchCombineString: string,
  tableClass?: string
}

interface DunsAllowedCountry {
  "baseUrl": string;
  "name": string;
}


const bdHeaders = ["businessDirectory.companyName", "businessDirectory.industry", "businessDirectory.siteType", "businessDirectory.location"]
const cdHeaders = ["contactDirectory.contactName", "contactDirectory.table_title", "contactDirectory.companyName", "contactDirectory.location"]

const SearchResult = ({
  resultData,
  paginationComponent,
  forComponent,
  inputSearchCombineString,
  tableClass
}: SearchResultProps): JSX.Element => {
	const {t} = useTranslation();
  const { countryCode } = usePageURL();

  const dunsAllowedCountryArray: DunsAllowedCountry[] = [
    {"baseUrl": `/duns-number/lookup.html`, "name": "united states"},
    {"baseUrl": `${ukDunsNumberUrl}/duns-number/lookup/find-my-duns.html`, "name": "united kingdom"},
    {"baseUrl": `/ca-en/duns-number/lookup/find-my-duns.html`, "name": "canada"},
    {"baseUrl": `/ie/duns-number/lookup/find-my-duns.html`, "name": "ireland"},
  ];
  
  const location = (data) => {
    const {
      addressCountry,
      addressLocality,
      addressRegion
    } = data;
  
    return <div>
      {addressLocality.length > 0 ? `${addressLocality}, ` : ""}
  
      {addressRegion.length > 0 ? `${addressRegion}, ` : ""}
  
      {addressCountry}
    </div>;
  };

  const noDataFoundUi = () => {
    if (inputSearchCombineString === "") return;

    let noResultText = "";

    noResultText = t(`${forComponent}.thereAreNoResultsForLocalized`)
    .replace("${search_text}", `${decodeURIComponent(inputSearchCombineString)}`);

    return (
      <div className="noResultBox">
        {noResultText}
        <div className="noResultBoxAdditionalText">
          <div>- {t("businessDirectory.noResultSuggestionOne")}</div>
          <div>- {t("businessDirectory.noResultSuggestionTwo")}</div>
        </div>
      </div>
    );
  };

  const getEmailDunsNumberUrl = (company: Company) => {
    const companyCompanyCountryLowerCase = company.companyCountry!.toLowerCase();

    const dunsAllowedCountry = dunsAllowedCountryArray.find((du) => du.name === companyCompanyCountryLowerCase);
    const query = new URLSearchParams({
      companyAddress: company.companyAddress,
      companyCity: company.companyCity,
      companyCountry: company.companyCountry,
      companyName: company.primaryName,
      companyRegion: company.companyRegion,
      companyZipCode: company.companyZipCode ?? "",
      refNumber: company.duns,
    });
    const baseUrl = dunsAllowedCountry?.baseUrl;
    return isNotUndefined(dunsAllowedCountry)
        ? `${baseUrl}?${query.toString()}`
        : void 0;
};

  const renderBusinessDirectoryData = (data) => {
    return data.companies.map(
      (co: Company, i: number) => {
        const {
          companyAddress,
          companyCity,
          companyRegion,
          companyZipCode,
          duns,
          industryName,
          locationType,
          primaryName,
          tradeStyleNames,
          companyProfileLink,
        } = co;

        const coCountryRegion = co.countryRegion;
        const companyInformationForCookie: CompanyInformationForCookie = {
          "companyAddress": companyAddress,
          "companyCity": companyCity,
          "companyName": primaryName,
          "companyState": companyRegion,
          "companyZip": companyZipCode,
          "completedCreditSignalFlow": false,
          "encryptedDuns": duns,
      };

        return (
          <tr className="no-bg" key={`company-${i}`}>
            <td className="td-div">
              <Link
                className="tableCompanyNameLink"
                to={companyProfileLink?.endsWith('.html') ? companyProfileLink : `${companyProfileLink}.html`}
              >
                <span>{primaryName}</span>
                <br />
              </Link>

              {tradeStyleNames && tradeStyleNames.length > 0 ? (
                <div className="tableTradeStyleName">
                  {tradeStyleNames}
                </div>
              ) : (
                <></>
              )}
              <a
                className="tableBusinessCreditReportsLink"
                // href={getBusinessDirectoryUrl(companyInformationForCookie)}
                 href={ countryCode == 'de' || countryCode == 'at' ?  `${t("businessDirectory.businessCreditLink")}${duns}` :  `${t("businessDirectory.businessCreditLink")}`}
                // onClick={() => setCompanyInfoCookie(companyInformationForCookie)}
              >
                {t("businessDirectory.businessCreditReport")}
              </a>
              

              <div className="tableLinksSpacer" />

              {(() => {
              const href = getEmailDunsNumberUrl(co);

              return isNotUndefined(href) ? (
                  <a
                      className={"tableEmailDunsNumberLink"}
                      href={href}
                      onClick={() => setCompanyInfoCookie(companyInformationForCookie)}
                  >
                      Email D-U-N-S Number
                  </a>
              ) : (
                  <></>
              );
          })()}
            </td>

            <td>
              <span className="tableIndustryName">
                {industryName}
              </span>
            </td>

            <td>
              <span className="tableLocationType">
                {locationType}
              </span>
            </td>

            <td>
              <span className="tableCountryRegion">
                {coCountryRegion}
              </span>
            </td>
          </tr>
        );
      }
    )
  }

  const renderContactDirectoryData = (data) => {
    return data.contacts.map(
      (res: ISearchContact, i: number) => {
        return (
          <tr className="no-bg" key={`company-${i}`}>
            <td className="td-div">
            <Link 
              to={`/contact-directory/contact-profile.${res.url}.html`}
              className="tableCompanyNameLink"
            >
              {res.givenName} {res.familyName}
            </Link>
             <div className="tableLinksSpacer" />
            </td>
            <td>
              <span className="tableIndustryName">
                {res.title}
              </span>
            </td>
            <td>
              <span className="tableLocationType">
                {res.organizationName}
              </span>
            </td>
            <td>
              <span className="tableCountryRegion">
                {location(res)}
              </span>
            </td>
          </tr>
        );
      }
    )
  }
  
  if (!resultData) return <></>;

  const tableHeaders = forComponent === "businessDirectory" ? bdHeaders : cdHeaders;
  const tableCheck = forComponent === "businessDirectory" ? (resultData.totalMatchedResults > 0 &&
    resultData.companies.length > 0) : (resultData.totalPages > 0 && resultData.contacts.length > 0);
  
  return (
    <>
      <div className="colorAccentWrapper">
        {/* <div className="colorAccent" /> */}
        <div className={forComponent === "contactDirectoryIndustry" ? "crawl-component-container" : "searchResultsContainer"}>
          {forComponent !== "contactDirectoryIndustry" &&
            <SearchHeading
              text={forComponent === "businessDirectory" ?
                t("businessDirectory.searchResultHeading") : t("contactDirectory.searchResultHeading")
              }
            />
          }
          <div className={forComponent === "contactDirectoryIndustry" ? "container" : ""}>
            <div className={forComponent === "contactDirectoryIndustry" ? "crawl-search-result-component-container" : ""}>
              {tableCheck ? <>
                <div className="table-container">
                  {/* <table className={forComponent === "contactDirectory" ? "searchDataTable" : ""}> */}
                  <table className={tableClass}>
                    <thead>
                      <tr>
                        {tableHeaders.map(header => (
                          <th role="columnheader">
                            <span className={forComponent === "businessDirectory" ? "tableHeader" : ""}>
                              {t(header)}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {forComponent === "businessDirectory" ?
                        renderBusinessDirectoryData(resultData)
                        : renderContactDirectoryData(resultData)
                      }
                    </tbody>
                  </table>
                </div>
                {paginationComponent}
              </> : noDataFoundUi()
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResult;